import dynamic from 'next/dynamic';
import { memo, useCallback } from 'react';

import { ShortUrl } from '@air/api';
import { Spinner } from '@air/primitive-spinner';
import { useShortIdContext } from '@air/provider-short-id';

import { PublicAnnotationComment } from '~/components/AnnotationsV2/AnnotationsAvatarsContainer/AnnotationAvatarContainer/components/PublicAnnotationComment';
import { PublicAnnotationsAvatars } from '~/components/AnnotationsV2/AnnotationsAvatarsContainer/components/PublicAnnotationsAvatars';
import { useCanAnnotateCentralizedPublicClip } from '~/components/AnnotationsV2/hooks/useCanAnnotateCentralizedPublicClip';
import { PublicFloatingCommentForm } from '~/components/AnnotationsV2/shared/PublicFloatingCommentForm';
import { type PDFVisualizerProps } from '~/components/AssetModalV2/Visualizer/PDFVisualizer/PDFVisualizer';
import { usePublicPermissions } from '~/components/PublicPermissionsProvider';
import { useExternalLinksClickableContext } from '~/providers/ExternalLinksClickableProvider';
import { centralizedClipIdSelector } from '~/store/centralizedClip/selectors';
import { usePublicLinkAddOns } from '~/swr-hooks/workspaces/addOns/usePublicLinkAddOns';
import { canViewAddOns } from '~/utils/permissions/workspacePermissions';
import { useAirSelector } from '~/utils/ReduxUtils';

const PDFVisualizer = dynamic<PDFVisualizerProps>(
  () =>
    import('~/components/AssetModalV2/Visualizer/PDFVisualizer/PDFVisualizer')
      .then((mod) => mod.PDFVisualizer)
      .catch((e) => {
        console.error('ERROR', e);
        return () => <div className="flex size-full items-center justify-center text-white">Something went wrong</div>;
      }),
  {
    ssr: false,
    loading: () => (
      <div className="flex size-full items-center justify-center">
        <Spinner className="size-8 ads:text-ads-cyan-10 text-teal-9" />
      </div>
    ),
  },
);

export const PublicPDFVisualizer = memo(() => {
  const { canEditAnnotation } = useCanAnnotateCentralizedPublicClip();
  const { permissions } = usePublicPermissions();
  const { shortId } = useShortIdContext();
  const clipId = useAirSelector(centralizedClipIdSelector); // clipId may not be in url if it is public clip link
  const { data: addOns } = usePublicLinkAddOns({ shortId, enabled: canViewAddOns(permissions) });
  const { areExternalLinksClickable } = useExternalLinksClickableContext();

  const isDocumentIntelligenceAddOnEnabled = !!addOns?.data.some(
    (addOn) => addOn.key === 'documentIntelligence' && addOn.enabled && addOn.visible,
  );

  const getFallbackPDFUrl = useCallback(async () => {
    if (!clipId) {
      return '';
    }
    const { url } = await ShortUrl.getDownloadUrl(shortId, clipId);

    return url;
  }, [clipId, shortId]);

  return (
    <PDFVisualizer
      AnnotationsAvatars={PublicAnnotationsAvatars}
      AnnotationComment={PublicAnnotationComment}
      FloatingCommentForm={PublicFloatingCommentForm}
      getFallbackPDFUrl={getFallbackPDFUrl}
      canAnnotateClip={canEditAnnotation}
      insertSearchFromQuery={isDocumentIntelligenceAddOnEnabled}
      hideInternalOutline
      hideZoomAndOutlineControls
      areExternalLinksEnabled={areExternalLinksClickable}
    />
  );
});

PublicPDFVisualizer.displayName = 'PublicPDFVisualizer';
